<template>
	<div class="preselection-wrapper">
		<!-- Header - Solo si no es menú -->
		<div v-if="!showAsMenu" class="preselection-header">
			<div class="header-left">
				<span class="header-icon">
					<v-icon>mdi-account-search</v-icon>
				</span>
				<div>
					<h3 class="header-title">Preselección de Subcontratistas</h3>
					<p class="header-subtitle">Antes de asignar a proyectos</p>
				</div>
			</div>
			<v-btn
				v-if="showButton"
				small
				outlined
				:color="showListing ? '#959595' : '#FF120B'"
				@click="showListing = !showListing"
			>
				<v-icon left>mdi-information-outline</v-icon>
				{{ showListing ? 'Cerrar Listado de subcontratistas' : 'Abrir Listado de subcontratistas' }}
			</v-btn>
			<div v-else class="header-badge">{{ users.length }} <span>candidatos</span></div>
		</div>

		<!-- Table -->
		<v-menu
			v-if="showAsMenu"
			offset-y
			:close-on-content-click="false"
			max-width="800px"
		>
			<template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
						<v-btn
							outlined
							dark
							small
							color="#959595"
							v-bind="{ ...tooltipAttrs, ...menuAttrs }"
							v-on="{ ...tooltipOn, ...menuOn }"
						>
							<v-icon dark small class="mr-2">mdi-account-group</v-icon>
							<p class="mb-0 caption">Historial Preselección</p>
						</v-btn>
					</template>
					<span>Historial Preselección de Subcontratistas</span>
				</v-tooltip>
			</template>
			<v-card class="menu-card">
				<!-- Search Input -->
				<div style="padding: 12px 16px; border-bottom: 1px solid #ebebeb;">
					<v-text-field
						v-model="searchQuery"
						dense
						outlined
						hide-details
						placeholder="Buscar por nombre o correo..."
						prepend-inner-icon="mdi-magnify"
						clearable
						class="search-field"
					></v-text-field>
				</div>
				<div class="table-container">
					<!-- Column Headers -->
					<div class="table-head">
						<div class="col-user">Subcontratista</div>
						<div class="col-status">Preselección</div>
						<div class="col-notes">Notas</div>
					</div>

					<!-- Rows -->
					<div
						v-for="(user, index) in filteredUsers"
						:key="'sc-' + user.id"
						class="table-row"
						:class="{ 'row-alt': index % 2 !== 0 }"
						@click="startChat(user)"
						title="Click para abrir chat"
					>
						<!-- Avatar + Info -->
						<div class="col-user user-cell">
							<div class="avatar-wrap">
								<img
									v-if="getUserAvatar(user)"
									:src="getUserAvatar(user)"
									class="avatar-img"
								/>
								<div
									v-else
									class="avatar-fallback"
									:style="{ background: getUserTypeColor(user.userType) + '18', color: getUserTypeColor(user.userType) }"
								>
									{{ getInitials(getFullName(user)) }}
								</div>
							</div>
							<div class="user-info">
								<span class="user-name">{{ getFullName(user) }}</span>
								<span class="user-email">{{ user.email }}</span>
							</div>
							<div class="chat-hint">
								<v-icon small>mdi-message-outline</v-icon>
								<span>Chatear</span>
							</div>
						</div>

						<!-- Status Select -->
						<div class="col-status" @click.stop>
							<div class="status-select-wrap">
								<div
									class="status-dot"
									:style="{ background: getStateColor(userStates[user.id]) }"
								></div>
								<v-select
									:value="userStates[user.id]"
									:items="stateOptions"
									dense
									outlined
									hide-details
									item-value="value"
									item-text="label"
									placeholder="Seleccione"
									class="status-select"
									@input="setProjectSelection(user.id, $event)"
								>
									<template v-slot:selection="{ item }">
										<span class="status-label" :style="{ color: getStateColor(item.value) }">
											{{ item.label }}
										</span>
									</template>
									<template v-slot:item="{ item }">
										<div class="status-option">
											<div class="status-dot" :style="{ background: getStateColor(item.value) }"></div>
											<span>{{ item.label }}</span>
										</div>
									</template>
								</v-select>
							</div>
						</div>

						<!-- Notes -->
						<div class="col-notes" @click.stop>
							<v-menu v-model="notesMenuOpen[user.id]" offset-y min-width="280px" :close-on-content-click="false" nudge-left="20">
								<template v-slot:activator="{ on, attrs }">
									<button
										class="notes-btn"
										:class="{ 'has-notes': !!userNotes[user.id] }"
										v-bind="attrs"
										v-on="on"
										@click="currentUserNotes = user"
									>
										<v-icon small>
											{{ userNotes[user.id] ? 'mdi-note-text' : 'mdi-note-plus-outline' }}
										</v-icon>
										<span>{{ userNotes[user.id] ? 'Ver nota' : 'Agregar' }}</span>
									</button>
								</template>
								<v-card class="notes-card" elevation="4">
									<div class="notes-card-header">
										<v-icon small dark>mdi-note-text</v-icon>
										<span>Notas — {{ getFullName(user) }}</span>
									</div>
									<v-card-text style="padding: 12px;">
										<v-textarea
											v-model="userNotes[user.id]"
											dense
											outlined
											rows="4"
											hide-details
											placeholder="Escribe tus notas sobre este subcontratista..."
											class="notes-textarea"
											@blur="$emit('notes-changed', { userId: user.id, notes: userNotes[user.id] })"
										></v-textarea>
										<div class="d-flex align-center justify-end mt-2">
											<v-btn
												text
												small
												color="grey darken-2"
												@click="closeNotesMenu(user.id)"
											>
												Cancelar
											</v-btn>
											<v-btn
												outlined
												small
												@click="saveNotes(user.id)"
											>
												Guardar nota
											</v-btn>
										</div>
									</v-card-text>
								</v-card>
							</v-menu>
						</div>
					</div>

					<!-- Empty State -->
					<div v-if="filteredUsers.length === 0" class="empty-state">
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5">
							<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
							<circle cx="9" cy="7" r="4"/>
							<path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
							<path d="M16 3.13a4 4 0 0 1 0 7.75"/>
						</svg>
						<p>No hay subcontratistas disponibles</p>
					</div>
				</div>
			</v-card>
		</v-menu>

		<div v-else-if="showListing" class="expanded-table-wrapper">
			<!-- Search Input -->
			<div style="padding: 12px 16px; border-bottom: 1px solid #ebebeb;">
				<v-text-field
					v-model="searchQuery"
					dense
					outlined
					hide-details
					placeholder="Buscar por nombre o correo..."
					prepend-inner-icon="mdi-magnify"
					clearable
					class="search-field"
				></v-text-field>
			</div>
			<div class="table-container">
				<!-- Column Headers -->
				<div class="table-head">
					<div class="col-user">Subcontratista</div>
					<div class="col-status">Preselección</div>
					<div class="col-notes">Notas</div>
				</div>

				<!-- Rows -->
				<div
					v-for="(user, index) in filteredUsers"
					:key="'sc-' + user.id"
				class="table-row"
				:class="{ 'row-alt': index % 2 !== 0 }"
				@click="startChat(user)"
				title="Click para abrir chat"
			>
				<!-- Avatar + Info -->
				<div class="col-user user-cell">
					<div class="avatar-wrap">
						<img
							v-if="getUserAvatar(user)"
							:src="getUserAvatar(user)"
							class="avatar-img"
						/>
						<div
							v-else
							class="avatar-fallback"
							:style="{ background: getUserTypeColor(user.userType) + '18', color: getUserTypeColor(user.userType) }"
						>
							{{ getInitials(getFullName(user)) }}
						</div>
					</div>
					<div class="user-info">
						<span class="user-name">{{ getFullName(user) }}</span>
						<span class="user-email">{{ user.email }}</span>
					</div>
					<div class="chat-hint">
						<v-icon small>mdi-message-outline</v-icon>
						<span>Chatear</span>
					</div>
				</div>

				<!-- Status Select -->
				<div class="col-status" @click.stop>
					<div class="status-select-wrap">
						<div
							class="status-dot"
							:style="{ background: getStateColor(userStates[user.id]) }"
						></div>
						<v-select
							:value="userStates[user.id]"
							:items="stateOptions"
							dense
							outlined
							hide-details
							item-value="value"
							item-text="label"
							placeholder="Seleccione"
							class="status-select"
							@input="setProjectSelection(user.id, $event)"
						>
							<template v-slot:selection="{ item }">
								<span class="status-label" :style="{ color: getStateColor(item.value) }">
									{{ item.label }}
								</span>
							</template>
							<template v-slot:item="{ item }">
								<div class="status-option">
									<div class="status-dot" :style="{ background: getStateColor(item.value) }"></div>
									<span>{{ item.label }}</span>
								</div>
							</template>
						</v-select>
					</div>
				</div>

				<!-- Notes -->
				<div class="col-notes" @click.stop>
					<v-menu v-model="notesMenuOpen[user.id]" offset-y min-width="280px" :close-on-content-click="false" nudge-left="20">
						<template v-slot:activator="{ on, attrs }">
							<button
								class="notes-btn"
								:class="{ 'has-notes': !!userNotes[user.id] }"
								v-bind="attrs"
								v-on="on"
								@click="currentUserNotes = user"
							>
								<v-icon small>
									{{ userNotes[user.id] ? 'mdi-note-text' : 'mdi-note-plus-outline' }}
								</v-icon>
								<span>{{ userNotes[user.id] ? 'Ver nota' : 'Agregar' }}</span>
							</button>
						</template>
						<v-card class="notes-card" elevation="4">
							<div class="notes-card-header">
								<v-icon small dark>mdi-note-text</v-icon>
								<span>Notas — {{ getFullName(user) }}</span>
							</div>
							<v-card-text style="padding: 12px;">
								<v-textarea
									v-model="userNotes[user.id]"
									dense
									outlined
									rows="4"
									hide-details
									placeholder="Escribe tus notas sobre este subcontratista..."
									class="notes-textarea"
									@blur="$emit('notes-changed', { userId: user.id, notes: userNotes[user.id] })"
								></v-textarea>
								<div class="d-flex align-center justify-end mt-2">
									<v-btn
										text
										small
										color="grey darken-2"
										@click="closeNotesMenu(user.id)"
									>
										Cancelar
									</v-btn>
									<v-btn
										outlined
										small
										@click="saveNotes(user.id)"
									>
										Guardar nota
									</v-btn>
								</div>
							</v-card-text>
						</v-card>
					</v-menu>
				</div>
			</div>

			<!-- Empty State -->
			<div v-if="filteredUsers.length === 0" class="empty-state">
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5">
					<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
					<circle cx="9" cy="7" r="4"/>
					<path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
					<path d="M16 3.13a4 4 0 0 1 0 7.75"/>
				</svg>
				<p>No hay subcontratistas disponibles</p>
			</div>
			</div>
		</div>
	</div>
</template>

<script>
import { createUserChatRoom } from '@/helpers/api/chats'
import { setProjectSelectionToSubcontractor, getProjectSelectionToSubcontractor } from '@/helpers/api/costs'

const TYPE_CONFIG = {
	client:                { label: 'Cliente',                      color: '#111111', groupLabel: 'Cliente' },
	project_subcontractor: { label: 'Subcontratista del Proyecto',  color: '#524d4d', groupLabel: 'Subcontratistas del Proyecto' },
	subcontractor:         { label: 'Subcontratista',               color: '#524d4d', groupLabel: 'Subcontratistas' }
}

const STATE_OPTIONS = [
	{ value: 1, label: 'Recomendado', color: '#22C55E' },
	{ value: 2, label: 'Apto',     color: '#F59E0B' },
	{ value: 3, label: 'Descartado',   color: '#EF4444' }
]

export default {
	name: "SubcontractorPreselectionChat",
	props: {
		project: {
			type: Object,
			required: true
		},
		users: {
			type: Array,
			default: () => []
		},
		showButton: {
			type: Boolean,
			default: false
		},
		showAsMenu: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showListing: !this.showButton,
			searchQuery: '',
			userStates: {},
			userNotes: {},
			currentUserNotes: null,
			notesMenuOpen: {},
			stateOptions: STATE_OPTIONS
		}
	},
	computed: {
		filteredUsers() {
			if (!this.searchQuery.trim()) {
				return this.users
			}
			const query = this.searchQuery.toLowerCase()
			return this.users.filter(user => {
				const fullName = this.getFullName(user).toLowerCase()
				const email = (user.email || '').toLowerCase()
				return fullName.includes(query) || email.includes(query)
			})
		}
	},
	mounted(){
		this.getProjectSelection()
	},
	methods: {
		getFullName(user) {
			const profile = user?.profile || user
			return [profile?.name, profile?.lastname, profile?.second_lastname]
				.filter(Boolean)
				.join(' ')
		},
		getInitials(name) {
			if (!name) return '?'
			return name.split(' ').map(w => w.charAt(0).toUpperCase()).slice(0, 2).join('')
		},
		getUserAvatar(user) {
			return user?.profile?.avatar || user?.avatar
		},
		getUserTypeColor(userType) {
			return TYPE_CONFIG[userType]?.color || '#959595'
		},
		getStateColor(state) {
			const s = STATE_OPTIONS.find(o => o.value === state)
			return s ? s.color : '#aaa'
		},
		setUserState(userId, state) {
			this.$set(this.userStates, userId, state)
			this.$emit('state-changed', { userId, state })
		},
		async startChat(user) {
			try {
				const userChat = await createUserChatRoom({ user_id: user.id })
				if (userChat && userChat.code === 200 && userChat.chat_room) {
					this.$store.commit('general/addChatRoomCenter', userChat.chat_room)
				} else {
					this.setMessage({
						message: 'No fue posible crear la sala de chat. Intenta nuevamente.',
						title: 'Error al crear chat',
						type: 'danger',
						icon: 'mdi-alert-circle',
						color: '#FF1209'
					})
				}
			} catch (error) {
				console.error('Error al iniciar chat:', error)
				this.setMessage({
					message: error.message || 'Error desconocido al iniciar el chat',
					title: 'Error al crear chat',
					type: 'danger',
					icon: 'mdi-alert-circle',
					color: '#FF1209'
				})
			}
		},
		async getProjectSelection(){
			const response = await getProjectSelectionToSubcontractor({
				project_line_id: this.project.project_line_info?.project_line_id
			})
			if(response.code == 200){
				const selections = response.preselect || []
				const states = {}
				const notes = {}
				selections.forEach(s => {
					states[s.subcontractor_id] = s.qualification
					notes[s.subcontractor_id] = s.observations
				})
				this.userStates = states
				this.userNotes = notes
			}
		},
		async setProjectSelection(subcontractor_id, qualification) {
			try {
				this.$set(this.userStates, subcontractor_id, qualification)
				const response = await setProjectSelectionToSubcontractor({
					project_line_id: this.project.project_line_info?.project_line_id,
					subcontractor_id,
					qualification
				})
				if (response && response.code === 200) {
					this.setMessage({
						message: 'Selección de proyecto actualizada correctamente.',
						title: 'Éxito',
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#22C55E'
					})
				} else {
					this.setMessage({
						message: 'No fue posible actualizar la selección de proyecto. Intenta nuevamente.',
						title: 'Error',
						type: 'danger',
						icon: 'mdi-alert-circle',
						color: '#FF1209'
					})
				}
			} catch (error) {
				console.error('Error al actualizar la selección de proyecto:', error)
				this.setMessage({
					message: error.message || 'Error desconocido al actualizar la selección de proyecto',
					title: 'Error',
					type: 'danger',
					icon: 'mdi-alert-circle',
					color: '#FF1209'
				})
			}
		},
		async saveNotes(subcontractor_id) {
			const observations = this.userNotes[subcontractor_id] || ''
			const response = await setProjectSelectionToSubcontractor({
				project_line_id: this.project.project_line_info?.project_line_id,
				subcontractor_id,
				observations
			})
			if (response && response.code === 200) {
				this.closeNotesMenu(subcontractor_id)
				this.setMessage({
					message: 'Notas guardadas correctamente.',
					title: 'Éxito',
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#22C55E'
				})
			} else {
				this.setMessage({
					message: 'No fue posible guardar las notas. Intenta nuevamente.',
					title: 'Error',
					type: 'danger',
					icon: 'mdi-alert-circle',
					color: '#FF1209'
				})
			}
		},
		closeNotesMenu(userId) {
			this.$set(this.notesMenuOpen, userId, false)
			this.currentUserNotes = null
		},
    setMessage(messageData){
      const data = {
        message: messageData.message,
        title: messageData.title,
        created: new Date(),
        type: messageData.type,
        icon: messageData.icon,
        color: messageData.color,
      }
      this.$store.dispatch('general/addMessage', data)
    },
	}
}
</script>

<style scoped>
/* ── Base ─────────────────────────────────────────────────── */
.preselection-wrapper {
	font-family: 'DM Sans', 'Helvetica Neue', sans-serif;
	padding: 0px;
	color: #1a1a1a;
}

/* ── Header ───────────────────────────────────────────────── */
.preselection-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 20px;
}

.header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.header-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: #f0eeee;
	color: #524d4d;
	flex-shrink: 0;
}

.header-title {
	margin: 0 0 2px 0;
	font-size: 16px;
	font-weight: 700;
	letter-spacing: -0.3px;
	color: #1a1a1a;
}

.header-subtitle {
	margin: 0;
	font-size: 12px;
	color: #888;
}

.header-badge {
	font-size: 13px;
	font-weight: 700;
	color: #524d4d;
	background: #f0eeee;
	border-radius: 20px;
	padding: 4px 12px;
	letter-spacing: -0.2px;
}

.header-badge span {
	font-weight: 400;
	color: #888;
	font-size: 12px;
}

/* ── Table ────────────────────────────────────────────────── */
.menu-card {
	max-height: 500px;
	overflow-y: auto;
}

/* ── Search Field ─────────────────────────────────────────── */
.search-field >>> fieldset {
	border-color: #e8e8e8 !important;
	border-radius: 6px !important;
}

.search-field >>> .v-input__control .v-input__slot {
	min-height: 36px !important;
}

.search-field >>> .v-input__prepend-inner {
	margin-right: 8px !important;
}

.search-field >>> input {
	font-size: 13px !important;
}

.table-container {
	border: 1px solid #ebebeb;
	border-radius: 10px;
	overflow: hidden;
}

.table-head {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	background: #fafafa;
	border-bottom: 1px solid #ebebeb;
	gap: 12px;
}

.table-head > div {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.6px;
	color: #aaa;
}

/* ── Row ──────────────────────────────────────────────────── */
.table-row {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	gap: 12px;
	border-bottom: 1px solid #f2f2f2;
	cursor: pointer;
	transition: background 0.15s ease;
	position: relative;
}

.table-row:last-child {
	border-bottom: none;
}

.table-row:hover {
	background: #f7f5f5 !important;
}

.table-row:hover .chat-hint {
	opacity: 1;
	transform: translateX(0);
}

.row-alt {
	background: #fdfdfd;
}

/* ── Column widths ────────────────────────────────────────── */
.col-user   { flex: 1; min-width: 0; }
.col-status { width: 160px; flex-shrink: 0; }
.col-notes  { width: 110px; flex-shrink: 0; display: flex; justify-content: flex-end; }

/* ── User cell ────────────────────────────────────────────── */
.user-cell {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}

.avatar-wrap {
	flex-shrink: 0;
}

.avatar-img,
.avatar-fallback {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	object-fit: cover;
}

.avatar-fallback {
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
}

.user-info {
	min-width: 0;
	flex: 1;
}

.user-name {
	display: block;
	font-size: 13px;
	font-weight: 600;
	color: #1a1a1a;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.3;
}

.user-email {
	display: block;
	font-size: 11px;
	color: #aaa;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.3;
}

/* Chat hint that appears on hover */
.chat-hint {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 11px;
	color: #524d4d;
	font-weight: 500;
	opacity: 0;
	transform: translateX(-4px);
	transition: opacity 0.15s ease, transform 0.15s ease;
	flex-shrink: 0;
	white-space: nowrap;
}

/* ── Status select ────────────────────────────────────────── */
.status-select-wrap {
	display: flex;
	align-items: center;
	gap: 8px;
}

.status-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	flex-shrink: 0;
}

.status-label {
	font-size: 12px;
	font-weight: 500;
}

.status-option {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	padding: 2px 0;
}

.status-select >>> fieldset {
	border-color: #e8e8e8 !important;
}

.status-select >>> .v-input__control .v-input__slot {
	min-height: 32px !important;
	padding: 0 8px !important;
}

.status-select >>> .v-input__control {
	font-size: 12px;
}

/* ── Notes button ─────────────────────────────────────────── */
.notes-btn {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 11px;
	font-weight: 500;
	color: #999;
	background: #f5f5f5;
	border: 1px solid #ebebeb;
	border-radius: 6px;
	padding: 5px 10px;
	cursor: pointer;
	transition: all 0.15s ease;
	white-space: nowrap;
}

.notes-btn:hover {
	background: #efefef;
	color: #524d4d;
	border-color: #d0d0d0;
}

.notes-btn.has-notes {
	background: #f0eeee;
	color: #524d4d;
	border-color: #cac7c7;
}

/* ── Notes card ───────────────────────────────────────────── */
.notes-card {
	border-radius: 10px !important;
	overflow: hidden;
}

.notes-card-header {
	display: flex;
	align-items: center;
	gap: 8px;
	background: #2e2a2a;
	color: #f0eeee;
	font-size: 12px;
	font-weight: 600;
	padding: 10px 14px;
}

.notes-textarea >>> fieldset {
	border-color: #e8e8e8 !important;
	border-radius: 6px !important;
}

.notes-textarea >>> textarea {
	font-size: 12px !important;
	line-height: 1.6 !important;
}

/* ── Empty state ──────────────────────────────────────────── */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 52px 16px;
	color: #ccc;
}

.empty-state p {
	margin: 0;
	font-size: 13px;
}
</style>